import { useForm, type UseFormReturn } from '@kanda-libs/ks-component-ts';
import {
  useToast,
  type StringIndexedObject,
} from '@kanda-libs/ks-design-library';
import { actions } from '@kanda-libs/ks-frontend-services';
import { useAppDispatch } from 'components/App';
import useApiError from 'hooks/useApiError';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMyAccountCompanyInformationContext } from '../../context';
import { getTradingNameFormBody } from './helpers';

export interface TradingNameFormValues {
  trading_name: string | undefined;
}

export interface TradingNameFormHook {
  form: UseFormReturn<TradingNameFormValues>;
  onSubmit: (formValues: StringIndexedObject<TradingNameFormValues>) => void;
  isLoading: boolean;
  isSubmitting: boolean;
}

export default function useTradingNameForm(): TradingNameFormHook {
  const { company, isLoading, isSubmitting } =
    useMyAccountCompanyInformationContext();
  const dispatch = useAppDispatch();
  const onError = useApiError('Error updating VAT number at this time');
  const { showSuccess } = useToast();

  const defaultValues = useMemo(
    () => ({
      trading_name: company?.contact_info?.trading_name,
    }),
    [company],
  );

  const form = useForm<TradingNameFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues,
  });

  const onSubmit = useCallback(
    (formValues: StringIndexedObject<TradingNameFormValues>) => {
      const fv = formValues as unknown as TradingNameFormValues;
      if (!company || !company.id) return;
      const { id } = company;
      const body = getTradingNameFormBody(company, fv);
      if (!body) return;
      dispatch(
        actions.putCompany({
          params: { id },
          body,
          onError,
          onSuccess: () => {
            showSuccess('Trading name updated');
          },
        }),
      );
    },
    [company, dispatch, onError, showSuccess],
  );

  const initRef = useRef<boolean>(false);
  useEffect(() => {
    if (initRef.current || !company || isLoading) return;
    initRef.current = true;
    form.reset(defaultValues);
  }, [company, isLoading, defaultValues, form]);

  return {
    form,
    onSubmit,
    isLoading,
    isSubmitting,
  };
}
